import { createContext, useState, useMemo, useContext, useEffect, useCallback } from 'react';

const ReportesContext = createContext();

// GetResumenProduccionDiaria devuelve la fecha como texto "MM/DD/YYYY HH:mm:ss" (no es un
// DateTime, así que no pasa por el CorrectDateTimeConverter que emitiría ISO). Se normaliza
// a YYYY-MM-DD, que es lo que consume el informe. Se acepta ISO por si el back lo corrige.
function aFechaIso(valor) {
    const texto = String(valor ?? '').trim();
    const usa = texto.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (usa) {
        const [, mes, dia, anio] = usa;
        return `${anio}-${mes.padStart(2, '0')}-${dia.padStart(2, '0')}`;
    }
    return texto.slice(0, 10);
}

function getFirstEnabledIndex() {
    if (process.env.REACT_APP_ALLOW_REPORTS_INFORME_PRODUCCION === 'true') return 8;
    if (process.env.REACT_APP_ALLOW_REPORTS_OPERACION === 'true') return 9;
    if (process.env.REACT_APP_ALLOW_REPORTS_PRODUCTION_REPORT === 'true') return 1;
    if (process.env.REACT_APP_ALLOW_REPORTS_CORRIDAS_REPORT === 'true') return 2;
    if (process.env.REACT_APP_ALLOW_REPORTS_LOST_TIMES === 'true') return 3;
    if (process.env.REACT_APP_ALLOW_REPORTS_MTBF_MTTR === 'true') return 4;
    if (process.env.REACT_APP_ALLOW_REPORTS_TIMELINE === 'true') return 5;
    if (process.env.REACT_APP_ALLOW_REPORTS_INFORME2 === 'true') return 7;
    if (process.env.REACT_APP_ALLOW_REPORTS_TOP_LLAMADAS === 'true') return 10;
    if (process.env.REACT_APP_ALLOW_REPORTS_PRODUCCION_DIARIA === 'true') return 11;
    return 1;
}

export function ReportesProvider(props) {
    const tokenSessionObject = JSON.parse(sessionStorage.getItem('token'));
    const [selectedD, setSelectedD] = useState(new Date());
    const [fechaIni, setFechaIni] = useState(new Date());
    const [fechaFin, setFechaFin] = useState();
    const [selectedIndex, setSelectedIndex] = useState(getFirstEnabledIndex);
    const [posIni, setPosIni] = useState(1);
    const [posFin, setPosFin] = useState(16);
    const [skuOrder, setSkuOrder] = useState([]);
    const [corridas, setCorridas] = useState([]);
    const [listOrds, setListOrds] = useState([]);
    const [flag, setFlag] = useState(false);
    const [txtRange, setTxtRange] = useState('');
    const [open, setOpen] = useState(false);
    const [celdas, setCeldas] = useState([]);
    const [tPerdidos, setTPerdido] = useState([]);
    const [tPerdidosPvNP, setTPerdidosPvNP] = useState([]);
    const [detallePlane, setDetallePlane] = useState([]);
    const [detalleNoPlane, setDetalleNoPlane] = useState([]);
    const [periodo, setPeriodo] = useState(3);
    const [celda, setCelda] = useState(7);
    const [selector, setSelector] = useState(1);
    const [sumParos, setSumParos] = useState([]);
    const [data, setData] = useState([]);
    const [xData, setXData] = useState([]);
    const [indicadores, setIndicadores] = useState([]);
    const [dataU, setDataU] = useState([]);
    const [dataE, setDataE] = useState([]);
    const [dataMtbf, setDataMtbf] = useState([]);
    const [dataMttr, setDataMttr] = useState([]);
    const [dataX, setDataX] = useState([]);
    const [fechaHoraIni, setFechaHoraIni] = useState('');
    const [fechaHoraFin, setFechaHoraFin] = useState('');
    const [statusLinea, setStatusLinea] = useState([]);
    const [dataP, setDataP] = useState([]);
    const [dataP1, setDataP1] = useState([]);
    const [dataL, setDataL] = useState([]);
    const [minX, setMinX] = useState(0);
    const [maxX, setMaxX] = useState(0);
    const [dataPie, setDataPie] = useState([]);
    const [isLoadingLlamadas, setIsLoadingLlamadas] = useState(false);
    const [areas, setAreas] = useState([]);
    const [linesByArea, setLinesByArea] = useState([]);
    const [hierCeldas, setHierCeldas] = useState([]);
    const [selectedArea, setSelectedAreaState] = useState(null);
    const [selectedLinea, setSelectedLineaState] = useState(null);
    const [selectedCelda, setSelectedCeldaState] = useState(null);
    const [informeProduccionData, setInformeProduccionData] = useState([]);
    const [reporteOperacionData, setReporteOperacionData] = useState(null);
    const [tPerdidosFetched, setTPerdidosFetched] = useState(false);
    const [exportFn, setExportFn] = useState(null);
    const [topLlamadas, setTopLlamadas] = useState({ topIncidencias: [], topDuracion: [] });
    const [topN, setTopN] = useState(10);
    const [areasAtencion, setAreasAtencion] = useState([]);
    const [areaAtencion, setAreaAtencion] = useState(-1);
    const [esDowntime, setEsDowntime] = useState(-1);
    const [informeProduccionDiariaData, setInformeProduccionDiariaData] = useState([]);

    const fillTxtRange = useCallback(() => {
        if (fechaFin !== undefined) {
            setTxtRange(
                `${fechaIni.getDate()}/${fechaIni.getMonth() + 1
                }/${fechaIni.getFullYear()}-${fechaFin.getDate()}/${fechaFin.getMonth() + 1
                }/${fechaFin.getFullYear()}`
            );
        }
    }, [fechaFin, fechaIni]);

    const getAreas = useCallback(async () => {
        try {
            const res = await fetch(`${process.env.REACT_APP_SERVICE_ENDPOINT}/areas`, {
                method: 'GET',
                headers: {
                    authorization: `Bearer ${tokenSessionObject.token}`,
                    Accept: 'application/json',
                },
            });
            if (res.ok) setAreas(await res.json());
        } catch { }
    }, [tokenSessionObject.token]);

    const setSelectedArea = useCallback(async (area) => {
        setSelectedAreaState(area);
        setSelectedLineaState(null);
        setSelectedCeldaState(null);
        setHierCeldas([]);
        setLinesByArea([]);
        if (!area) return;
        try {
            const res = await fetch(
                `${process.env.REACT_APP_SERVICE_ENDPOINT}/lineas?area=${area.area1}`,
                {
                    method: 'GET',
                    headers: {
                        authorization: `Bearer ${tokenSessionObject.token}`,
                        Accept: 'application/json',
                    },
                }
            );
            if (res.ok) setLinesByArea(await res.json());
        } catch { }
    }, [tokenSessionObject.token]);

    const setSelectedLinea = useCallback(async (linea) => {
        setSelectedLineaState(linea);
        setSelectedCeldaState(null);
        setHierCeldas([]);
        if (!linea) return;
        try {
            const res = await fetch(
                `${process.env.REACT_APP_SERVICE_ENDPOINT}/CeldaPorLinea/CeldaPorLinea?linea=${linea.id}`,
                {
                    method: 'GET',
                    headers: {
                        authorization: `Bearer ${tokenSessionObject.token}`,
                        Accept: 'application/json',
                    },
                }
            );
            if (res.ok) {
                const raw = await res.json();
                setHierCeldas(
                    raw
                        .flatMap((l) => l.celdas || [])
                        .filter((c) => c.orden !== 0)
                        .map((c) => ({ id: c.celda, nombre: c.nombreCelda }))
                );
            }
        } catch { }
    }, [tokenSessionObject.token]);

    const setSelectedCelda = useCallback((celda) => {
        setSelectedCeldaState(celda);
    }, []);

    const getReporteOperacion = useCallback(async ({ fecha, linea = 0 } = {}) => {
        if (process.env.REACT_APP_ALLOW_REPORTS_OPERACION !== 'true') return;
        if (!fecha) return;
        setIsLoadingLlamadas(true);
        try {
            const fechaISO = new Date(`${fecha}T00:00:00`).toISOString();
            const res = await fetch(
                `${process.env.REACT_APP_SERVICE_ENDPOINT}/Informes/GetReporteOperacionDiaria`,
                {
                    method: 'POST',
                    body: JSON.stringify({ fecha: fechaISO, linea, offsetMinutos: -new Date().getTimezoneOffset() }),
                    headers: {
                        authorization: `Bearer ${tokenSessionObject.token}`,
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                    },
                }
            );
            if (!res.ok) return;
            const data = await res.json();
            setReporteOperacionData(data);
        } catch {
            // network or parse error
        } finally {
            setIsLoadingLlamadas(false);
        }
    }, [tokenSessionObject.token]);

    const getInformeProduccion = useCallback(async ({ lineas = [], sku = '', orden = '' } = {}) => {
        if (process.env.REACT_APP_ALLOW_REPORTS_INFORME_PRODUCCION !== 'true') return;
        if (!fechaIni || !fechaFin) return;

        const dIni = new Date(fechaIni.getFullYear(), fechaIni.getMonth(), fechaIni.getDate(), 0, 0, 0, 0).toISOString();
        const dFin = new Date(fechaFin.getFullYear(), fechaFin.getMonth(), fechaFin.getDate() + 1, 0, 0, 0, 0).toISOString();

        setIsLoadingLlamadas(true);
        try {
            const res = await fetch(
                `${process.env.REACT_APP_SERVICE_ENDPOINT}/Informes/getReporteProduccion`,
                {
                    method: 'POST',
                    body: JSON.stringify({ fechaIni: dIni, fechaFin: dFin, lineas, sku, orden }),
                    headers: {
                        authorization: `Bearer ${tokenSessionObject.token}`,
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                    },
                }
            );
            if (!res.ok) return;
            const data = await res.json();
            if (Array.isArray(data)) {
                setInformeProduccionData(data.map((item, i) => ({ ...item, id: i })));
            }
        } catch {
            // network or parse error
        } finally {
            setIsLoadingLlamadas(false);
        }
    }, [fechaIni, fechaFin, tokenSessionObject.token]);

    const getInformeProduccionDiaria = useCallback(async () => {
        if (process.env.REACT_APP_ALLOW_REPORTS_PRODUCCION_DIARIA !== 'true') return;
        if (!fechaIni || !fechaFin) return;

        const dIni = new Date(fechaIni.getFullYear(), fechaIni.getMonth(), fechaIni.getDate(), 0, 0, 0, 0).toISOString();
        const dFin = new Date(fechaFin.getFullYear(), fechaFin.getMonth(), fechaFin.getDate() + 1, 0, 0, 0, 0).toISOString();

        setIsLoadingLlamadas(true);
        try {
            const res = await fetch(
                `${process.env.REACT_APP_SERVICE_ENDPOINT}/Informes/GetResumenProduccionDiaria`,
                {
                    method: 'POST',
                    body: JSON.stringify({
                        fechaIni: dIni,
                        fechaFin: dFin,
                        linea: selectedLinea ? selectedLinea.id : 0,
                        offsetMinutos: -new Date().getTimezoneOffset(),
                    }),
                    headers: {
                        authorization: `Bearer ${tokenSessionObject.token}`,
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                    },
                }
            );
            if (!res.ok) return;
            const data = await res.json();
            // Un renglón por (día, SKU). Miguel documentó los campos en PascalCase porque son los
            // nombres de columna SQL; el backend los serializa en camelCase (JsonNamingPolicy.CamelCase).
            if (Array.isArray(data)) {
                setInformeProduccionDiariaData(
                    data.map((item) => ({
                        fecha: aFechaIso(item.fecha),
                        linea: item.linea,
                        sku: item.sku ?? '',
                        nombre: item.nombre ?? '',
                        meta: Number(item.meta) || 0,
                        unidadesProducidas: Number(item.unidadesProducidas) || 0,
                    }))
                );
            }
        } catch {
            // network or parse error
        } finally {
            setIsLoadingLlamadas(false);
        }
    }, [fechaIni, fechaFin, selectedLinea, tokenSessionObject.token]);

    const getAreasAtencion = useCallback(async () => {
        if (process.env.REACT_APP_ALLOW_REPORTS_TOP_LLAMADAS !== 'true') return;
        try {
            const res = await fetch(
                `${process.env.REACT_APP_SERVICE_ENDPOINT}/MicroServiciosController?categoria=AreasAtencion`,
                {
                    method: 'GET',
                    headers: {
                        authorization: `Bearer ${tokenSessionObject.token}`,
                        Accept: 'application/json',
                    },
                }
            );
            if (!res.ok) return;
            const obj = await res.json();
            if (Array.isArray(obj)) setAreasAtencion(obj);
        } catch {
            // network or parse error
        }
    }, [tokenSessionObject.token]);

    const getTopLlamadas = useCallback(async () => {
        if (process.env.REACT_APP_ALLOW_REPORTS_TOP_LLAMADAS !== 'true') return;
        if (!fechaIni || !fechaFin) return;

        const dIni = new Date(fechaIni.getFullYear(), fechaIni.getMonth(), fechaIni.getDate(), 0, 0, 0, 0).toISOString();
        const dFin = new Date(fechaFin.getFullYear(), fechaFin.getMonth(), fechaFin.getDate() + 1, 0, 0, 0, 0).toISOString();

        setIsLoadingLlamadas(true);
        try {
            const res = await fetch(
                `${process.env.REACT_APP_SERVICE_ENDPOINT}/Informes/GetTopLlamadas`,
                {
                    method: 'POST',
                    body: JSON.stringify({
                        topN,
                        fechaIni: dIni,
                        fechaFin: dFin,
                        linea: selectedLinea ? [selectedLinea.id] : [],
                        celda: selectedCelda ? [selectedCelda.id] : [],
                        areaAtencion,
                        esDowntime,
                    }),
                    headers: {
                        authorization: `Bearer ${tokenSessionObject.token}`,
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                    },
                }
            );
            if (!res.ok) return;
            const data = await res.json();
            setTopLlamadas({
                topIncidencias: Array.isArray(data?.topIncidencias) ? data.topIncidencias : [],
                topDuracion: Array.isArray(data?.topDuracion) ? data.topDuracion : [],
            });
        } catch {
            // network or parse error
        } finally {
            setIsLoadingLlamadas(false);
        }
    }, [topN, fechaIni, fechaFin, selectedLinea, selectedCelda, areaAtencion, esDowntime, tokenSessionObject.token]);

    const filteredLines = useMemo(
        () => (selectedArea ? linesByArea : []),
        [selectedArea, linesByArea]
    );

    const getLineaTiempoHist = useCallback(async () => {
        if (!fechaIni || !fechaFin) return;

        const dIni = new Date(fechaIni.getFullYear(), fechaIni.getMonth(), fechaIni.getDate(), 0, 0, 0, 0).toISOString();
        const dFin = new Date(fechaFin.getFullYear(), fechaFin.getMonth(), fechaFin.getDate() + 1, 0, 0, 0, 0).toISOString();

        setIsLoadingLlamadas(true);
        try {
            const res = await fetch(
                `${process.env.REACT_APP_SERVICE_ENDPOINT}/Paros/GetEstatusLineasHist`,
                {
                    method: 'POST',
                    body: JSON.stringify({
                        linea: selectedLinea?.id ?? 0,
                        fechaHoraIni: dIni,
                        fechaHoraFin: dFin,
                    }),
                    headers: {
                        authorization: `Bearer ${tokenSessionObject.token}`,
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                    },
                }
            );
            if (!res.ok) return;
            const respuesta = await res.json();

            const estatusNombre = (dato) => {
                if (dato.estatus === 0) return 'Fuera de Operacion';
                if (dato.estatus === 1) return 'Operando';
                if (dato.estatus === 2) return 'Setup';
                if (dato.estatus === 3) return 'Purga';
                if (dato.estatus === 4) return 'Purga y Lavado';
                if (dato.estatus === 5) return 'Paro Planeado';
                if (dato.estatus === 7) return 'Sin Material';
                if (dato.estatus === 8) return 'Atasco de Línea';
                return 'Paro';
            };

            const estLineaT = [];
            if (Array.isArray(respuesta)) {
                respuesta.forEach((resp) => {
                    const datos = resp.datos.map((dato) => ({
                        folio: dato.folio,
                        estatus: dato.estatus,
                        nombreEstatus: estatusNombre(dato),
                        fechaHoraIni: dato.fechaHoraIni,
                        fechaHoraFin: dato.fechaHoraFin,
                    }));
                    estLineaT.push({ celda: resp.celda, nombre: resp.nombre, datos });
                });
            }
            setStatusLinea(estLineaT);
        } catch (e) {
            // network or parse error
        } finally {
            setIsLoadingLlamadas(false);
        }
    }, [tokenSessionObject.token, fechaIni, fechaFin, selectedLinea]);

    const getLlamadas = useCallback(async () => {
        if (process.env.REACT_APP_ALLOW_REPORTS_LOST_TIMES !== 'true') {
            return;
        }
        if (!fechaHoraIni || !fechaHoraFin) {
            return;
        }

        setIsLoadingLlamadas(true);
        try {
            function msToHMS(ms) {
                // 1- Convert to seconds:
                let seconds = ms / 1000;
                // 2- Extract hours:
                const hours = parseInt(seconds / 3600); // 3,600 seconds in 1 hour
                seconds = seconds % 3600; // seconds remaining after extracting hours
                // 3- Extract minutes:
                const minutes = parseInt(seconds / 60); // 60 seconds in 1 minute
                // 4- Keep only seconds not extracted to minutes:
                seconds = seconds % 60;
                return `${agregarCeroSiEsNecesario(hours)}:${agregarCeroSiEsNecesario(
                    minutes
                )}:${agregarCeroSiEsNecesario(Math.floor(seconds))}`;
            }

            const getLineaTiempo = async (calls) => {
                const data = {
                    fechaHoraIni: new Date(fechaHoraIni).toISOString(),
                    fechaHoraFin: new Date(fechaHoraFin).toISOString(),
                    linea: selectedLinea?.id ?? 0,
                };
                const lineaTiempo = await fetch(
                    `${process.env.REACT_APP_SERVICE_ENDPOINT}/Paros/GetEstatusLineasHist`,
                    {
                        method: 'POST',
                        body: JSON.stringify(data),
                        headers: {
                            authorization: `Bearer ${tokenSessionObject.token}`,
                            Accept: 'application/json',
                            'Content-Type': 'application/json',
                        },
                    }
                );
                if (!lineaTiempo.ok) return;
                const respuesta = await lineaTiempo.json();
                if (lineaTiempo.status === 200) {
                    let estLinea = [];
                    let estLineaT = [];
                    let nEstatus;

                    let data1 = [];
                    let data2 = [];
                    let fechaIni = 0;
                    let fechaFin = 0;
                    let diff = 0;
                    let min = null;
                    let max = null;

                    let valuePie = [0, 0, 0, 0, 0, 0];

                    if (Array.isArray(respuesta) && respuesta.length > 0) {
                        respuesta.forEach((resp) => {
                            if (resp.celda !== 1) {
                                resp.datos.forEach((dato) => {
                                    if (dato.estatus === 0) {
                                        nEstatus = 'Fuera de Operacion';
                                        valuePie[0] = valuePie[0] + 1;
                                    } else if (dato.estatus === 1) {
                                        nEstatus = 'Operando';
                                        valuePie[1] = valuePie[1] + 1;
                                    } else if (dato.estatus === 2) {
                                        nEstatus = 'Setup';
                                        valuePie[2] = valuePie[2] + 1;
                                    } else if (dato.estatus === 3) {
                                        nEstatus = 'Purga';
                                        valuePie[3] = valuePie[3] + 1;
                                    } else if (dato.estatus === 4) {
                                        valuePie[4] = valuePie[4] + 1;
                                        nEstatus = 'Purga y Lavado';
                                    } else if (dato.estatus === 5) {
                                        nEstatus = 'Paro planeado';
                                        valuePie[5] = valuePie[5] + 1;
                                    } else if (dato.estatus === 7) {
                                        nEstatus = 'Sin Material';
                                        valuePie[5] = valuePie[5] + 1;
                                    } else if (dato.estatus === 8) {
                                        nEstatus = 'Atasco de Línea';
                                        valuePie[5] = valuePie[5] + 1;
                                    } else if (
                                        dato.estatus === 6 ||
                                        dato.estatus >= 9 ||
                                        dato.estatus < 0
                                    ) {
                                        valuePie[5] = valuePie[5] + 1;
                                        let nombreE = calls.find(
                                            (llamada) => llamada.tipoLlamada === dato.estatus
                                        );
                                        if (nombreE === undefined || nombreE.tipoLlamada === 10) {
                                            nEstatus = 'Paro';
                                        } else {
                                            nEstatus = nombreE.nombre;
                                        }
                                    }
                                    estLinea.push({
                                        folio: dato.folio,
                                        estatus: dato.estatus,
                                        nombreEstatus: nEstatus,
                                        fechaHoraFin: dato.fechaHoraFin,
                                        fechaHoraIni: dato.fechaHoraIni,
                                    });
                                    fechaIni = Date.parse(dato.fechaHoraIni);
                                    fechaFin = Date.parse(dato.fechaHoraFin);

                                    diff = msToHMS(fechaFin - fechaIni);
                                    if (min !== null) {
                                        if (fechaIni < min) {
                                            min = fechaIni;
                                        }
                                    } else {
                                        min = fechaIni;
                                    }

                                    if (max !== null) {
                                        if (fechaFin > max) {
                                            max = fechaFin;
                                        }
                                    } else {
                                        max = fechaFin;
                                    }
                                    data1.push({
                                        name: nEstatus,
                                        data: [
                                            {
                                                x: resp.nombre,
                                                y: [
                                                    Date.parse(dato.fechaHoraIni),
                                                    Date.parse(dato.fechaHoraFin),
                                                ],
                                                z: [dato.estatus, diff, dato.folio],
                                            },
                                        ],
                                    });
                                    if (resp.celda === 2) {
                                        data2.push({
                                            name: nEstatus,
                                            data: [
                                                {
                                                    x: resp.nombre,
                                                    y: [
                                                        Date.parse(dato.fechaHoraIni),
                                                        Date.parse(dato.fechaHoraFin),
                                                    ],
                                                    z: [dato.estatus, diff, dato.folio],
                                                },
                                            ],
                                        });
                                    }
                                });
                                estLineaT.push({
                                    celda: resp.celda,
                                    nombre: resp.nombre,
                                    datos: estLinea,
                                });
                            }

                            estLinea = [];
                        });
                    }
                    setDataP(data1);
                    setDataP1(valuePie);
                    setMinX(min);
                    setMaxX(max);
                    setStatusLinea(estLineaT);
                    setDataL(data2);
                }
            };

            const res = await fetch(`${process.env.REACT_APP_SERVICE_ENDPOINT}/tiposllamada/Listado`, {
                method: 'GET',
                headers: {
                    authorization: `Bearer ${tokenSessionObject.token}`,
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
            });
            if (!res.ok) return;
            const calls = await res.json();

            if (Math.floor(res.status / 100) === 2) {
                await getLineaTiempo(calls);
            }
        } catch (e) {
            // network or parse error
        } finally {
            setIsLoadingLlamadas(false);
        }
    }, [tokenSessionObject.token, fechaHoraIni, fechaHoraFin, selectedLinea]);

    const transformDate = useCallback((base) => {
        let mes = '';
        if (base.getMonth() + 1 < 10) mes = `0${(base.getMonth() + 1).toString()}`;
        else mes = (base.getMonth() + 1).toString();
        let dia = '';
        if (base.getDate() < 10) dia = `0${base.getDate().toString()}`;
        else dia = base.getDate().toString();

        return `${base.getFullYear()}-${mes}-${dia}`;
    }, []);

    const setWeek = useCallback(async (selectedDate) => {
        setSelectedD(selectedDate);
        setFechaIni(new Date(selectedDate));
        setFechaFin(new Date(selectedDate));
    }, []);

    const handleClickF = useCallback(() => {
        const diaIni = parseInt(fechaIni.toISOString().slice(8, 10));
        const diaFin = parseInt(fechaFin.toISOString().slice(8, 10));

        if (diaIni <= 10) {
            setPosIni((fechaIni.getMonth() + 1) * 3 - 3);
        } else if (diaIni <= 20) {
            setPosIni((fechaIni.getMonth() + 1) * 3 - 2);
        } else {
            setPosIni((fechaIni.getMonth() + 1) * 3 - 1);
        }

        if (diaFin <= 10) {
            setPosFin((fechaFin.getMonth() + 1) * 3 - 2);
        } else if (diaFin <= 20) {
            setPosFin((fechaFin.getMonth() + 1) * 3 - 1);
        } else {
            setPosFin((fechaFin.getMonth() + 1) * 3);
        }
    }, [fechaFin, fechaIni]);

    const getReports = useCallback(async () => {
        if (process.env.REACT_APP_ALLOW_REPORTS_PRODUCTION_REPORT !== 'true') {
            return;
        }

        try {
            const getRepoSku = async () => {
                const inicio = new Date(fechaIni.getFullYear(), fechaIni.getMonth(), fechaIni.getDate(), 0, 0, 0, 0).toISOString();
                const fin = new Date(fechaFin.getFullYear(), fechaFin.getMonth(), fechaFin.getDate() + 1, 0, 0, 0, 0).toISOString();

                const skuOrders = await fetch(
                    `${process.env.REACT_APP_SERVICE_ENDPOINT}/Report/sku?fechaIni=${inicio}&fechaFin=${fin}`,
                    {
                        method: 'GET',
                        headers: {
                            authorization: `Bearer ${tokenSessionObject.token}`,
                            Accept: 'application/json',
                        },
                    }
                );
                if (!skuOrders.ok) return;
                const repoSku = await skuOrders.json();
                const data = [];
                if (Array.isArray(repoSku)) {
                    repoSku.forEach((item, index) => {
                        const data1 = {
                            cantidad: item.cantidad,
                            estatus: item.estatus,
                            fecha: item.fecha.slice(0, 10),
                            ordenProd: item.ordenProd,
                            producido: item.producido,
                            sku: item.sku,
                            cliente: item.cliente,
                            id: index,
                        };
                        data.push(data1);
                    });
                }

                setSkuOrder(data);
            };

            const inicio = new Date(fechaIni.getFullYear(), fechaIni.getMonth(), fechaIni.getDate(), 0, 0, 0, 0).toISOString();
            const fin = new Date(fechaFin.getFullYear(), fechaFin.getMonth(), fechaFin.getDate() + 1, 0, 0, 0, 0).toISOString();

            const listOrders = await fetch(
                `${process.env.REACT_APP_SERVICE_ENDPOINT}/Ordenes/ListadoOrdenesFecha?start=${inicio}&end=${fin}`,
                {
                    method: 'GET',
                    headers: {
                        authorization: `Bearer ${tokenSessionObject.token}`,
                        Accept: 'application/json',
                    },
                }
            );
            if (!listOrders.ok) return;
            const ordersJS = await listOrders.json();
            const data = [];
            if (Array.isArray(ordersJS)) {
                ordersJS.forEach((item) => {
                    const point = {
                        x: item.ordenProd,
                        y: item.producido,
                        goals: [
                            {
                                name: 'Plan',
                                value: item.cantidad,
                                strokeWidth: 20,
                                strokeHeight: 0,
                                strokeLineCap: 'round',
                                strokeColor: '#775DD0',
                            },
                        ],
                    };
                    data.push(point);
                });
            }
            setListOrds(data);
            await getRepoSku();
            handleClickF();
        } catch (e) {
            // network or parse error
        }
    }, [fechaFin, fechaIni, handleClickF, tokenSessionObject.token]);

    const getCorridas = useCallback(async () => {
        if (process.env.REACT_APP_ALLOW_REPORTS_CORRIDAS_REPORT !== 'true') {
            return;
        }

        try {
            const data = {
                fechaIni: new Date(fechaIni.getFullYear(), fechaIni.getMonth(), fechaIni.getDate(), 0, 0, 0, 0).toISOString(),
                fechaFin: new Date(fechaFin.getFullYear(), fechaFin.getMonth(), fechaFin.getDate() + 1, 0, 0, 0, 0).toISOString(),
                linea: selectedLinea?.id ?? 0,
            };

            const corri = await fetch(
                `${process.env.REACT_APP_SERVICE_ENDPOINT}/Estadisticas/ReporteCorridas`,
                {
                    method: 'POST',
                    body: JSON.stringify(data),
                    headers: {
                        authorization: `Bearer ${tokenSessionObject.token}`,
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                    },
                }
            );
            if (!corri.ok) return;
            const respuesta = await corri.json();
            if (corri.status === 200) {
                let data = [];
                let fechaFinR;
                respuesta.forEach((item, index) => {
                    if (item.fechaFinReal === null) {
                        fechaFinR = null;
                    } else {
                        fechaFinR = item.fechaFinReal.slice(0, 10);
                    }
                    const data1 = {
                        corrida: item.corrida,
                        producido: item.producido,
                        piezasMalas: item.piezasMalas,
                        sku: item.sku,
                        ordenProd: item.ordenProd,
                        fechaIniReal: item.fechaIniReal.slice(0, 10),
                        fechaFinReal: fechaFinR,
                        id: index,
                    };
                    data.push(data1);
                });
                setCorridas(data);
            }
        } catch (e) {
            // network or parse error
        }
    }, [fechaFin, fechaIni, selectedLinea, tokenSessionObject.token]);

    const getCeldas = useCallback(async () => {
        if (process.env.REACT_APP_ALLOW_REPORTS_OEE_MODULE !== 'true') {
            return;
        }

        try {
            const data = await fetch(`${process.env.REACT_APP_SERVICE_ENDPOINT}/celdas`, {
                method: 'GET',
                headers: {
                    authorization: `Bearer ${tokenSessionObject.token}`,
                    Accept: 'application/json',
                },
            });
            if (!data.ok) return;
            const celdas = await data.json();
            if (data.status === 200) {
                if (celdas.length > 0) {
                    filtrarCeldas(celdas);
                }
            }
        } catch (e) {
            // network or parse error
        }
    }, [tokenSessionObject.token]);

    const filtrarCeldas = (cells) => {
        if (process.env.REACT_APP_ALLOW_REPORTS_OEE_MODULE !== 'true') {
            return;
        }
        const celdasF = [];

        cells.forEach((cell) => {
            if (cell.linea === 3) {
                celdasF.push(cell);
            }
        });
        setCeldas(celdasF);
    };

    const getPerdidos = useCallback(async () => {
        if (process.env.REACT_APP_ALLOW_REPORTS_LOST_TIMES !== 'true') {
            return;
        }

        try {
            const offsetMinutos = -new Date().getTimezoneOffset();
            const dFin = new Date(fechaFin.getFullYear(), fechaFin.getMonth(), fechaFin.getDate() + 1, 0, 0, 0);
            const data = {
                fechaHoraIni: new Date(fechaIni.getFullYear(), fechaIni.getMonth(), fechaIni.getDate(), 0, 0, 0).toISOString(),
                fechaHoraFin: dFin.toISOString(),
                offsetMinutos,
                ...(selectedArea && { area: selectedArea.area1 }),
                ...(selectedLinea && { linea: selectedLinea.id }),
                ...(selectedCelda && { celda: selectedCelda.id }),
            };
            const perdi = await fetch(
                `${process.env.REACT_APP_SERVICE_ENDPOINT}/Informes/GetPerdidos`,
                {
                    method: 'POST',
                    body: JSON.stringify(data),
                    headers: {
                        authorization: `Bearer ${tokenSessionObject.token}`,
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                    },
                }
            );
            if (!perdi.ok) return;
            const respuesta = await perdi.json();
            if (perdi.status === 200) {
                setTPerdido(respuesta);
            }
            const perdiPlanVsNoPLan = await fetch(
                `${process.env.REACT_APP_SERVICE_ENDPOINT}/Informes/GetPerdidosPlaneados`,
                {
                    method: 'POST',
                    body: JSON.stringify(data),
                    headers: {
                        authorization: `Bearer ${tokenSessionObject.token}`,
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                    },
                }
            );
            if (!perdiPlanVsNoPLan.ok) return;
            const respuesta1 = await perdiPlanVsNoPLan.json();
            if (perdiPlanVsNoPLan.status === 200) {
                setTPerdidosPvNP(respuesta1);
            }
            const detallePlaneados = await fetch(
                `${process.env.REACT_APP_SERVICE_ENDPOINT}/Informes/GetDetallePlaneados`,
                {
                    method: 'POST',
                    body: JSON.stringify(data),
                    headers: {
                        authorization: `Bearer ${tokenSessionObject.token}`,
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                    },
                }
            );
            if (!detallePlaneados.ok) return;
            const respuesta2 = await detallePlaneados.json();
            if (detallePlaneados.status === 200) {
                setDetallePlane(respuesta2);
            }

            const detalleNoPlaneados = await fetch(
                `${process.env.REACT_APP_SERVICE_ENDPOINT}/Informes/GetDetalleNoPlaneados`,
                {
                    method: 'POST',
                    body: JSON.stringify(data),
                    headers: {
                        authorization: `Bearer ${tokenSessionObject.token}`,
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                    },
                }
            );
            if (!detalleNoPlaneados.ok) return;
            const respuesta3 = await detalleNoPlaneados.json();
            if (detalleNoPlaneados.status === 200) {
                setDetalleNoPlane(respuesta3);
            }
        } catch (e) {
            // network or parse error
        } finally {
            setTPerdidosFetched(true);
        }
    }, [selectedArea, selectedCelda, fechaFin, fechaIni, selectedLinea, tokenSessionObject.token]);

    const getSumaParos = useCallback(async () => {
        if (process.env.REACT_APP_ALLOW_REPORTS_LOST_TIMES !== 'true') {
            return;
        }

        try {
            const offsetMinutos = -new Date().getTimezoneOffset();
            const dFin = new Date(fechaFin.getFullYear(), fechaFin.getMonth(), fechaFin.getDate() + 1, 0, 0, 0);
            const data = {
                fechaHoraIni: new Date(fechaIni.getFullYear(), fechaIni.getMonth(), fechaIni.getDate(), 0, 0, 0).toISOString(),
                fechaHoraFin: dFin.toISOString(),
                offsetMinutos,
                ...(selectedArea && { area: selectedArea.area1 }),
                ...(selectedLinea && { linea: selectedLinea.id }),
                ...(selectedCelda && { celda: selectedCelda.id }),
            };
            const sumParos = await fetch(
                `${process.env.REACT_APP_SERVICE_ENDPOINT}/Informes/GetSumatoriaParos`,
                {
                    method: 'POST',
                    body: JSON.stringify(data),
                    headers: {
                        authorization: `Bearer ${tokenSessionObject.token}`,
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                    },
                }
            );
            if (!sumParos.ok) return;
            const respuesta = await sumParos.json();
            if (sumParos.status === 200) {
                setSumParos(respuesta);
            }
        } catch (e) {
            // network or parse error
        }
    }, [selectedArea, selectedCelda, fechaFin, fechaIni, selectedLinea, tokenSessionObject.token]);

    const getIndicadores = useCallback(async () => {
        if (process.env.REACT_APP_ALLOW_REPORTS_KPI_REPORT !== 'true') {
            return;
        }

        try {
            const offsetMinutos = -new Date().getTimezoneOffset();
            const dFin = new Date(fechaFin.getFullYear(), fechaFin.getMonth(), fechaFin.getDate() + 1, 0, 0, 0);
            const data = {
                fechaHoraIni: new Date(fechaIni.getFullYear(), fechaIni.getMonth(), fechaIni.getDate(), 0, 0, 0).toISOString(),
                fechaHoraFin: dFin.toISOString(),
                offsetMinutos,
                ...(selectedArea && { area: selectedArea.area1 }),
                ...(selectedLinea && { linea: selectedLinea.id }),
                ...(selectedCelda && { celda: selectedCelda.id }),
            };
            const indica = await fetch(
                `${process.env.REACT_APP_SERVICE_ENDPOINT}/Informes/GetOEETEEPHist`,
                {
                    method: 'POST',
                    body: JSON.stringify(data),
                    headers: {
                        authorization: `Bearer ${tokenSessionObject.token}`,
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                    },
                }
            );
            if (!indica.ok) return;
            const respuesta = await indica.json();
            if (indica.status === 200) {
                setIndicadores(respuesta);
            }
        } catch (e) {
            // network or parse error
        }
    }, [selectedArea, selectedCelda, fechaFin, fechaIni, selectedLinea, tokenSessionObject.token]);

    const agregarCeroSiEsNecesario = (valor) => {
        if (valor < 10) {
            return `0${valor}`;
        } else {
            return `${valor}`;
        }
    };

    useEffect(() => {
        setWeek(new Date());
        getCeldas();
        getAreas();
        getAreasAtencion();
    }, [getCeldas, getAreas, getAreasAtencion, setWeek]);

    useEffect(() => {
        if (areas.length > 0 && !selectedArea) {
            setSelectedArea(areas[0]);
        }
    }, [areas, selectedArea, setSelectedArea]);

    useEffect(() => {
        if (flag === false && fechaFin !== undefined && selectedArea !== null) {
            fillTxtRange();
            // Solo disparar los servicios del informe activo al cargar la página
            if (selectedIndex === 1) getReports();
            else if (selectedIndex === 2) getCorridas();
            else if (selectedIndex === 4) getIndicadores();
            else if (selectedIndex === 7) getSumaParos();
            else if (selectedIndex === 10) getTopLlamadas();
            // index 3, 5, 8, 9: se disparan solo con "Ver Reporte"
            setFlag(true);
        } else {
            fillTxtRange();
        }
    }, [
        fechaFin,
        fillTxtRange,
        flag,
        getCorridas,
        getIndicadores,
        getLlamadas,
        getPerdidos,
        getReports,
        getSumaParos,
        getTopLlamadas,
        selectedArea,
        selectedIndex,
    ]);

    useEffect(() => {
        if (flag === true && fechaFin !== undefined) {
            setData([]);
            setXData([]);
            // Solo recargar los servicios del informe activo cuando cambia selector/periodo/fechaFin
            if (selectedIndex === 7) getSumaParos();
            else if (selectedIndex === 4) getIndicadores();
        }
    }, [fechaFin, flag, getIndicadores, getSumaParos, selector, selectedIndex]);

    const reportesValues = useMemo(() => {
        return {
            selectedD,
            setSelectedD,
            selectedIndex,
            setSelectedIndex,
            listOrds,
            handleClickF,
            setWeek,
            txtRange,
            fechaIni,
            setFechaIni,
            fechaFin,
            setFechaFin,
            getReports,
            transformDate,
            posIni,
            posFin,
            open,
            setOpen,
            skuOrder,
            getCorridas,
            corridas,
            celda,
            setCelda,
            celdas,
            setCeldas,
            periodo,
            setPeriodo,
            getPerdidos,
            tPerdidos,
            tPerdidosPvNP,
            detallePlane,
            setDetallePlane,
            detalleNoPlane,
            setDetalleNoPlane,
            selector,
            setSelector,
            sumParos,
            data,
            setData,
            xData,
            setXData,
            getSumaParos,
            indicadores,
            setIndicadores,
            getIndicadores,
            dataU,
            setDataU,
            dataE,
            setDataE,
            dataMtbf,
            setDataMtbf,
            dataMttr,
            setDataMttr,
            dataX,
            setDataX,
            fechaHoraFin,
            setFechaHoraFin,
            fechaHoraIni,
            setFechaHoraIni,
            dataP,
            setDataP,
            dataP1,
            minX,
            setMinX,
            maxX,
            setMaxX,
            statusLinea,
            getLlamadas,
            dataL,
            setDataL,
            dataPie,
            setDataPie,
            isLoadingLlamadas,
            getLineaTiempoHist,
            areas,
            filteredLines,
            hierCeldas,
            selectedArea,
            setSelectedArea,
            selectedLinea,
            setSelectedLinea,
            selectedCelda,
            setSelectedCelda,
            informeProduccionData,
            setInformeProduccionData,
            getInformeProduccion,
            reporteOperacionData,
            setReporteOperacionData,
            getReporteOperacion,
            tPerdidosFetched,
            setTPerdidosFetched,
            exportFn,
            setExportFn,
            topLlamadas,
            setTopLlamadas,
            getTopLlamadas,
            topN,
            setTopN,
            areasAtencion,
            areaAtencion,
            setAreaAtencion,
            esDowntime,
            setEsDowntime,
            informeProduccionDiariaData,
            setInformeProduccionDiariaData,
            getInformeProduccionDiaria,
        };
    }, [
        selectedD,
        setSelectedD,
        selectedIndex,
        setSelectedIndex,
        listOrds,
        handleClickF,
        setWeek,
        txtRange,
        fechaIni,
        setFechaIni,
        fechaFin,
        setFechaFin,
        getReports,
        transformDate,
        posIni,
        posFin,
        open,
        setOpen,
        skuOrder,
        getCorridas,
        corridas,
        celda,
        celdas,
        setCeldas,
        periodo,
        setPeriodo,
        getPerdidos,
        tPerdidos,
        tPerdidosPvNP,
        detallePlane,
        setDetallePlane,
        detalleNoPlane,
        setDetalleNoPlane,
        selector,
        setSelector,
        sumParos,
        data,
        setData,
        xData,
        setXData,
        getSumaParos,
        setCelda,
        indicadores,
        setIndicadores,
        getIndicadores,
        dataU,
        setDataU,
        dataE,
        setDataE,
        dataMtbf,
        setDataMtbf,
        dataMttr,
        setDataMttr,
        dataX,
        setDataX,
        fechaHoraFin,
        setFechaHoraFin,
        fechaHoraIni,
        setFechaHoraIni,
        dataP,
        setDataP,
        minX,
        setMinX,
        maxX,
        setMaxX,
        statusLinea,
        getLlamadas,
        dataL,
        dataP1,
        setDataL,
        dataPie,
        setDataPie,
        isLoadingLlamadas,
        getLineaTiempoHist,
        areas,
        filteredLines,
        hierCeldas,
        selectedArea,
        setSelectedArea,
        selectedLinea,
        setSelectedLinea,
        selectedCelda,
        setSelectedCelda,
        informeProduccionData,
        setInformeProduccionData,
        getInformeProduccion,
        reporteOperacionData,
        setReporteOperacionData,
        getReporteOperacion,
        tPerdidosFetched,
        setTPerdidosFetched,
        exportFn,
        setExportFn,
        topLlamadas,
        getTopLlamadas,
        topN,
        setTopN,
        areasAtencion,
        areaAtencion,
        esDowntime,
        informeProduccionDiariaData,
        getInformeProduccionDiaria,
    ]);

    return <ReportesContext.Provider value={reportesValues} {...props} />;
}

export function useReportes() {
    const context = useContext(ReportesContext);
    if (!context) {
        throw new Error('useFallas debe estar dentro del proveedor ReportesContext');
    }
    return context;
}
